import { type FormEvent, type ReactNode, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { z } from 'zod';
import { APP_NAME, waitlistSignupContract } from '@repo/shared/common';
import { triggerHaptic } from '@repo/shared/client';
import { AlertCircle } from '~/components/icons';
import {
  Alert,
  Button,
  Reveal,
  Stack,
  Text,
  TextInput,
  Title,
} from '~/components/ui';
import { createMutationHook } from '~/lib/query';

// Public request-access page. The marketing site's "Log in" / "Get started"
// buttons deep-link here; the visitor has no account, so this route lives under
// `_auth` (unauthenticated, no session gate — `_auth` only bounces *signed-in*
// users to the app). A two-step flow in one page: email → calm confirmation.
export const Route = createFileRoute('/_auth/request-access')({
  component: RequestAccessRoute,
});

const useWaitlistSignup = createMutationHook(waitlistSignupContract);
const EmailSchema = z.string().trim().email();

function RequestAccessRoute(): ReactNode {
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState<string | undefined>();
  const mutation = useWaitlistSignup();

  function handleSubmit(e: FormEvent): void {
    e.preventDefault();
    triggerHaptic();
    const result = EmailSchema.safeParse(email);
    if (!result.success) {
      setEmailError('Enter a valid email address.');
      return;
    }
    setEmailError(undefined);
    mutation.mutate({ body: { email: result.data, source: 'website' } });
  }

  if (mutation.isSuccess) {
    return <RequestAccessConfirmation />;
  }

  return (
    <form onSubmit={handleSubmit} noValidate>
      <Reveal variant="fade">
        <Stack gap="xl">
          <div>
            <Title order={1}>Request access</Title>
            <Text c="dimmed" size="sm" mt="xs">
              Enter your email and we will reach out when your access is ready.
            </Text>
          </div>

          {mutation.isError && (
            <Alert
              icon={<AlertCircle size={16} />}
              color="red"
              variant="light"
              data-testid="request-access-error"
            >
              Something went wrong. Please try again.
            </Alert>
          )}

          <TextInput
            label="Work email"
            type="email"
            value={email}
            onChange={(event) => setEmail(event.currentTarget.value)}
            error={emailError}
            placeholder="you@institution.com"
            autoComplete="email"
            autoFocus
            data-testid="request-access-email"
          />

          <Button
            type="submit"
            size="lg"
            fullWidth
            loading={mutation.isPending}
            data-testid="request-access-submit"
          >
            Next
          </Button>
        </Stack>
      </Reveal>
    </form>
  );
}

function RequestAccessConfirmation(): ReactNode {
  return (
    <Reveal variant="fade">
      <Stack gap="md" data-testid="request-access-confirmation">
        <Title order={1}>You&rsquo;re on the list.</Title>
        <Text c="dimmed" size="md">
          {APP_NAME} is invite-only while we onboard our first institutions. We
          will reach out when your access is ready.
        </Text>
      </Stack>
    </Reveal>
  );
}
